/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as HXKLineAndDynamicLineLayerSpec } from '../../../../layer/preset/hxKLine/hxKLineAndDynamicLine';
import { checkFieldType, createFieldValidator } from '../helper';
import * as utils from '../../../../util';

export type Spec = {
  layers: Partial<StandardChartLayerSpec>[];
};

type HXKLineAndDynamicLineLayerType = StandardChartLayerSpec &
  HXKLineAndDynamicLineLayerSpec & {
    __isEncodingValid?: boolean;
  };

/**
 * 检查K线图叠加动态折线数据类型是否符合要求
 * @param layer 图层配置
 * @param data 数据
 * @returns 数据类型是否符合要求
 */
function validateDataTypes(layer: HXKLineAndDynamicLineLayerType, data: Data): boolean {
  const { encoding, stocks } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length) {
    return false;
  }

  // 根据文档要求进行数据类型检测
  let isValid = true;

  // 检查X轴字段类型 - 必须是日期类型
  if (encoding.x && !checkFieldType(values, encoding.x, 'date')) {
    utils.logger.warn('warning', `The "x" field of the "${layer.type}" layer must be a date`);
    isValid = false;
  }

  // 检查Y轴字段类型 - 必须是数值类型
  const yFields = Array.isArray(encoding.y) ? encoding.y : [encoding.y];
  yFields.forEach(yField => {
    if (!checkFieldType(values, yField, 'number')) {
      utils.logger.warn('warning', `The "y" field of the "${layer.type}" layer must be a number`);
      isValid = false;
    }
  });

  // 检查股票数据配置
  if (!Array.isArray(stocks) || !stocks.length) {
    utils.logger.warn(
      'warning',
      `The "stocks" configuration is required for "${layer.type}" layer`
    );
    isValid = false;
  } else {
    stocks.forEach(stock => {
      if (!stock.code || !stock.market) {
        utils.logger.warn(
          'warning',
          `Each stock in "${layer.type}" layer must have both "code" and "market" properties`
        );
        isValid = false;
      }
    });
  }

  return isValid;
}

/**
 * 验证K线图叠加动态折线的编码配置
 * @param layer 图层配置
 * @returns 配置是否有效
 */
function validateEncoding(layer: HXKLineAndDynamicLineLayerType): boolean {
  return createFieldValidator('x')(layer) && createFieldValidator('y')(layer, true);
}

/**
 * K线图叠加动态折线数据处理
 * @param data 数据
 * @param spec 配置
 * @returns 是否有效
 */
export function hxKLineAndDynamicLine(data: Data, spec: Spec): boolean {
  let isValid = true;
  const { layers = [] } = spec;

  if (layers.every(layer => layer.type !== 'hxKLineAndDynamicLine')) {
    return isValid;
  }

  layers
    .filter(
      (layer): layer is HXKLineAndDynamicLineLayerType => layer.type === 'hxKLineAndDynamicLine'
    )
    .forEach(layer => {
      if (!validateEncoding(layer)) {
        isValid = false;
        layer.__isEncodingValid = false;
      }
      // 检查数据类型是否符合要求
      if (!validateDataTypes(layer, data)) {
        isValid = false;
        layer.__isEncodingValid = false;
        utils.logger.warn('warning', `Data type check failed for "${layer.type}" layer`);
      }

      // 检查数据量是否足够
      const dataIndex = layer.dataIndex || 0;
      const values = data[dataIndex]?.values || [];
      if (values.length < 2) {
        utils.logger.warn(
          'warning',
          `At least 2 data points are required for "${layer.type}" layer`
        );
        isValid = false;
      }

      // 检查日期范围是否有效
      const { encoding } = layer;
      if (encoding.x) {
        const dates = values.map(item => new Date(item[encoding.x]));
        const hasInvalidDate = dates.some(date => Number.isNaN(date.getTime()));
        if (hasInvalidDate) {
          utils.logger.warn('warning', `Invalid date format found in "${layer.type}" layer`);
          isValid = false;
        }
      }
    });

  return isValid;
}
